/**
 * Tabella di esposizione dell'API — DEFAULT-DENY.
 *
 * Risorse e metodi vengono dall'app standalone: `scripts/api_inventory.py`
 * legge i bridge C++ e produce `routes.generated.ts`. E' un contratto estratto
 * dal codice che gira in campo, non riscritto a memoria.
 *
 * LA REGOLA: un metodo non dichiarato qui NON e' raggiungibile — e non e' una
 * convenzione da ricordare in code review. `assertRoutingIsComplete()` gira
 * all'avvio e **impedisce al server di partire** se un handler registrato non
 * ha una rotta dichiarata, o se una rotta esposta non ha un handler.
 *
 * PERCHE' SERVE. Nell'app desktop il controllo dei permessi sta dentro i
 * bridge e la copertura non e' uniforme: chi puo' invocarli e' comunque gia'
 * seduto alla macchina e potrebbe aprire il file SQLite. Su un server
 * raggiungibile dai browser quella premessa cade. Caso concreto ereditato:
 * `FtpBridge::list()` restituisce la configurazione FTP **password compresa**
 * senza alcun controllo — non prende nemmeno SessionManager fra le dipendenze.
 */
import { ROUTES_GENERATED } from "./routes.generated.js";
import { Access } from "./types.js";
export { Access } from "./types.js";
export const ROUTES = ROUTES_GENERATED;
const index = new Map(ROUTES.map((r) => [`${r.resource}.${r.method}`, r]));
/** undefined = non dichiarato. Il chiamante DEVE negare. */
export function findRoute(resource, method) {
    return index.get(`${resource}.${method}`);
}
/** Le rotte raggiungibili dall'esterno (tutto tranne gli Internal). */
export function exposedRoutes() {
    return ROUTES.filter((r) => r.access !== Access.Internal);
}
/** Da confermare prima del campo: sono scelte di policy, non dati estratti. */
export function pendingPolicyReview() {
    return ROUTES.filter((r) => r.policyReview === true);
}
export class RoutingIncompleteError extends Error {
    undeclared;
    unimplemented;
    constructor(undeclared, unimplemented) {
        super([
            "Routing incompleto — il server non parte.",
            undeclared.length
                ? `  handler senza rotta dichiarata: ${undeclared.join(", ")}`
                : "",
            unimplemented.length
                ? `  rotte esposte senza handler: ${unimplemented.join(", ")}`
                : "",
            "  Dichiararle in routes (anche solo come Internal) prima di avviare.",
        ]
            .filter(Boolean)
            .join("\n"));
        this.undeclared = undeclared;
        this.unimplemented = unimplemented;
        this.name = "RoutingIncompleteError";
    }
}
/**
 * Il default-deny reso strutturale.
 *
 * Va chiamata all'avvio con le chiavi `"<resource>.<method>"` effettivamente
 * registrate. Solleva se qualcosa non torna, in entrambe le direzioni:
 *
 *  - un handler senza rotta: qualcuno ha aggiunto un metodo senza decidere se
 *    esporlo. La scelta sbagliata da fare in silenzio e' esporlo.
 *  - una rotta esposta senza handler: la UI riceverebbe un errore oscuro
 *    invece che al primo avvio, dove costa nulla.
 */
export function assertRoutingIsComplete(registered) {
    const have = new Set(registered);
    const undeclared = [];
    for (const key of have) {
        const [resource, ...rest] = key.split(".");
        if (!findRoute(resource, rest.join(".")))
            undeclared.push(key);
    }
    const unimplemented = exposedRoutes()
        .map((r) => `${r.resource}.${r.method}`)
        .filter((k) => !have.has(k));
    if (undeclared.length || unimplemented.length) {
        throw new RoutingIncompleteError(undeclared.sort(), unimplemented.sort());
    }
}
//# sourceMappingURL=routes.js.map